import React, {useEffect, useRef, useState} from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {RankingInfo, rankItem} from '@tanstack/match-sorter-utils'
import {Table} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {DisplayImage} from '../../widgets/components/General/DisplayImage'
import Close from '../../../../media/shared-access/ic-close-2.svg'
import {deleteInvestment} from '../core/_requests'
import {useAuth} from '../../auth'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({itemRank})
  return itemRank.passed
}

interface FinancialScenario {
  id: string
  stakeholdername: string
  email: string
  type: string
  existingInvestment: string | number
  newInvestment: string | number
}

export const DataTableServerSideFinancialScenario = ({
  shareholderInvestments,
  setShareholderInvestments,
}: {
  shareholderInvestments: FinancialScenario[]
  setShareholderInvestments: any
}) => {
  const {companyId} = useAuth()
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const {formatMessage} = useIntl()
  const [showKebabMenu, setShowKebabMenu] = useState(false) // eslint-disable-line
  const dropdownRef = useRef<HTMLDivElement>(null) // Ref for the dropdown menu
  const [pagination, setPagination] = useState({
    pageIndex: 0, // initial page index
    pageSize: 10, // default page size
  })
  const deleteNewInvestment = async (investmentId: string) => {
    try {
      const {
        data: {success},
      } = await deleteInvestment(companyId!, investmentId!)
      if (success) {
        setShareholderInvestments((prevInvestments: any) =>
          prevInvestments.filter((investment: any) => investment.id !== investmentId)
        )
      }
    } catch (err) {
      console.log(err)
    }
  }
  const columns = React.useMemo<ColumnDef<FinancialScenario>[]>(
    () => [
      {
        accessorKey: 'stakeholdername',
        id: 'stakeholdername',
        header: 'Stakeholder name',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'email',
        id: 'email',
        header: 'Email Id',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'type',
        id: 'type',
        header: 'Type',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'existingInvestment',
        id: 'existingInvestment',
        header: 'Existing Investment',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'newInvestment',
        id: 'newInvestment',
        header: 'New Investment',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
    ],
    []
  )

  useEffect(() => {
    // Function to handle clicks outside of the dropdown menu
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowKebabMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const table = useReactTable({
    data: shareholderInvestments,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      pagination,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: 'fuzzy',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })
  console.log(shareholderInvestments, ' shareholderInvestments')
  return (
    <>
      <div className='bg-white rounded-top p-4' style={{overflowX: 'auto'}}>
        <Table className='w-100' style={{minWidth: '600px'}}>
          <thead className='custom-thead border-bottom'>
            {shareholderInvestments &&
              table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan} className='min-w-120px pb-6 pt-4'>
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                  <th className='min-w-120px pb-6 pt-4'>{formatMessage({id: 'Actions'})}</th>
                </tr>
              ))}
          </thead>
          <tbody>
            {shareholderInvestments &&
              table.getRowModel().rows.map((row, index) => (
                <tr key={row.id} className='border-bottom'>
                  {row.getVisibleCells().map((cell, index) => (
                    <>
                      {index === 0 ? (
                        <td className='d-flex align-items-center border-0'>
                          <div className='d-flex align-items-center gap-2'>
                            <DisplayImage
                              imgName={'dummy'}
                              alt='profile'
                              fit='cover'
                              className='rounded-circle'
                              width={32}
                              height={32}
                            />
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        </td>
                      ) : index === 2 ? (
                        <td key={cell.id} className='pt-5 border-0'>
                          <div
                            className={`badge ${
                              flexRender(cell.column.columnDef.cell, cell.getContext()) ===
                              'Individual'
                                ? ' badge-warning'
                                : ' badge-success'
                            }`}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        </td>
                      ) : (
                        <td key={cell.id} className='pt-5 border-0'>
                          <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                        </td>
                      )}
                    </>
                  ))}
                  <td className='pt-5' onClick={(e) => e.stopPropagation()}>
                    <div className='d-flex justify-content-center'>
                      <button
                        type='button'
                        className='btn btn-none p-0 m-0'
                        onClick={() => deleteNewInvestment(row.original.id)}
                      >
                        <img
                          src={Close}
                          alt='Close-button'
                          style={{
                            filter: 'invert(30%) sepia(100%) saturate(500%) hue-rotate(-50deg)',
                          }}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
    </>
  )
}
